import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthorization } from '../contexts/AuthorizationContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../utils/cn';
import { Logo } from './ui';

/** Sidebar width tokens, mirrored to the `--sidebar-w` CSS var so Layout can
 * offset content without needing to know the collapsed state itself. */
const SIDEBAR_WIDTH_EXPANDED = '260px';
const SIDEBAR_WIDTH_COLLAPSED = '72px';

/* ── SVG icon helpers (inline to avoid MUI dependency) ── */
const Icon: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg className={cn('w-5 h-5 shrink-0', className)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const icons = {
  dashboard: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  globe: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418',
  dollar: 'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  folder: 'M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z',
  briefcase: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75a23.978 23.978 0 01-7.577-1.22 2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0',
  tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z',
  doc: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  shield: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  sun: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
  moon: 'M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z',
  logout: 'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9',
  user: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  chevronLeft: 'M15.75 19.5L8.25 12l7.5-7.5',
  chevronDown: 'M19.5 8.25l-7.5 7.5-7.5-7.5',
  checkCircle: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};


interface NavItem {
  label: string;
  path: string;
  icon: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const NavigationDFx5: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  const { isAdmin, canApproveStage } = useAuthorization();
  const isApprover = isAdmin || canApproveStage('TECHNICAL') || canApproveStage('OPERATIONS') || canApproveStage('SALES');
  const { isDark, toggle: toggleTheme } = useTheme();
  const [userEmail, setUserEmail] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ main: true, portfolio: true, admin: true });

  useEffect(() => {
    if (user) {
      user.getUserAttributes((err, attributes) => {
        if (err) { setUserEmail(user.getUsername()); return; }
        const email = attributes?.find(a => a.Name === 'email')?.Value || '';
        const given = attributes?.find(a => a.Name === 'given_name')?.Value || '';
        const family = attributes?.find(a => a.Name === 'family_name')?.Value || '';
        setUserEmail(`${given} ${family}`.trim() || email || user.getUsername());
      });
    } else {
      setUserEmail('');
    }
  }, [user]);

  // Collapse-offset fix: mirror the sidebar's own width to a CSS var so
  // Layout can track it (`ml-[var(--sidebar-w)]`) without prop-drilling
  // collapse state through the tree.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-w',
      collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED
    );
  }, [collapsed]);

  const mainNav: NavItem[] = [
    { label: 'Dashboard', path: '/', icon: 'dashboard' },
    { label: 'Proposals', path: '/loes', icon: 'list' },
    { label: 'Customers', path: '/customers', icon: 'users' },
    // SOW approvals also appear in a card on the dashboard, but this entry
    // links to a dedicated route so it can be its own distinct nav target.
    ...(isApprover ? [{ label: 'Approvals', path: '/approvals', icon: 'checkCircle' }] : []),
  ];

  const portfolioNav: NavItem[] = [
    { label: 'Offers', path: '/portfolio', icon: 'briefcase' },
    { label: 'Assets', path: '/sales-assets', icon: 'folder' },
  ];

  const adminNav: NavItem[] = [
    { label: 'Countries', path: '/countries', icon: 'globe' },
    { label: 'Rates', path: '/rates', icon: 'dollar' },
    { label: 'Roles', path: '/roles', icon: 'briefcase' },
    { label: 'LOE Types', path: '/loe-types', icon: 'tag' },
    { label: 'Admin Portal', path: '/admin', icon: 'shield' },
  ];

  const sections: NavSection[] = [
    { title: 'Main', items: mainNav },
    { title: 'Portfolio', items: portfolioNav },
    ...(isAdmin ? [{ title: 'Administration', items: adminNav }] : []),
  ];

  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const toggleSection = (title: string) => {
    setOpenSections(prev => ({ ...prev, [title.toLowerCase()]: !prev[title.toLowerCase()] }));
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    try { await signOut(); navigate('/signin'); }
    catch (e) { console.error('Logout failed:', e); }
  };

  if (!isAuthenticated && !isLoading) return null;

  return (
    <aside
      className={cn(
        'fixed top-0 left-0 h-screen z-40 flex flex-col transition-all duration-200',
        'bg-[var(--sidebar-bg)] border-r border-[var(--border)] text-[var(--foreground)]'
      )}
      style={{ width: 'var(--sidebar-w)' }}
    >
      {/* Logo + collapse */}
      <div className="flex items-center px-4 h-16 shrink-0 border-b border-[var(--border)]">
        {!collapsed ? (
          <>
            <RouterLink to="/" className="flex items-center group flex-1">
              <Logo withWordmark />
            </RouterLink>
            <button
              onClick={() => setCollapsed(true)}
              className="p-1.5 rounded-md text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-colors shrink-0"
              aria-label="Collapse sidebar"
            >
              <Icon d={icons.chevronLeft} className="w-4 h-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setCollapsed(false)}
            className="p-1.5 rounded-md text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-colors mx-auto"
            aria-label="Expand sidebar"
          >
            <Icon d={icons.chevronLeft} className="w-4 h-4 rotate-180" />
          </button>
        )}
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {sections.map(section => (
          <div key={section.title}>
            {!collapsed && (
              <button
                onClick={() => toggleSection(section.title)}
                className="flex items-center justify-between w-full px-2 mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
              >
                {section.title}
                <Icon
                  d={icons.chevronDown}
                  className={cn('w-3 h-3 transition-transform', !openSections[section.title.toLowerCase()] && '-rotate-90')}
                />
              </button>
            )}
            {(collapsed || openSections[section.title.toLowerCase()]) && (
              <ul className="space-y-0.5">
                {section.items.map(item => {
                  const active = isActive(item.path);
                  return (
                    <li key={item.path}>
                      <RouterLink
                        to={item.path}
                        title={collapsed ? item.label : undefined}
                        className={cn(
                          'relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
                          active
                            ? 'nav-item-active bg-[rgba(128,110,225,0.10)] text-[var(--foreground)]'
                            : 'text-[var(--foreground)] hover:bg-[var(--muted)]'
                        )}
                      >
                        {active && (
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full"
                            style={{ background: 'var(--brand-gradient)' }}
                          />
                        )}
                        <Icon
                          d={icons[item.icon as keyof typeof icons]}
                          className={active ? 'text-[var(--primary-600)]' : 'text-[var(--muted-foreground)]'}
                        />
                        {!collapsed && <span>{item.label}</span>}
                      </RouterLink>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </nav>

      {/* Bottom section: theme toggle + user */}
      <div className="shrink-0 border-t border-[var(--border)] p-2 space-y-1">
        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-all duration-150"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <Icon d={isDark ? icons.sun : icons.moon} />
          {!collapsed && <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        {/* User */}
        {isAuthenticated && (
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={cn(
                'flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm transition-all duration-150',
                userMenuOpen
                  ? 'bg-[var(--muted)] text-[var(--foreground)]'
                  : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]'
              )}
            >
              <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center text-xs font-semibold text-white shrink-0">
                {userEmail.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <span className="truncate text-left flex-1">{userEmail.split('@')[0]}</span>
              )}
            </button>

            {userMenuOpen && (
              <div className={cn(
                'absolute bottom-full mb-1 bg-[var(--card)] text-[var(--foreground)] rounded-lg shadow-xl border border-[var(--border)] overflow-hidden animate-scale-in z-50',
                collapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'
              )}>
                <div className="px-3 py-2 border-b border-[var(--border)]">
                  <p className="text-sm font-medium truncate">{userEmail}</p>
                </div>
                <button
                  onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-[var(--muted)] transition-colors flex items-center gap-2"
                >
                  <Icon d={icons.user} className="w-4 h-4" /> Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-3 py-2 text-left text-sm text-[var(--destructive)] hover:bg-[var(--muted)] transition-colors flex items-center gap-2"
                >
                  <Icon d={icons.logout} className="w-4 h-4" /> Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};

export default NavigationDFx5;
